import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchenDining } from "../../../redux/actions/kitchenDiningActions";
import moment from "moment";
import { useLanguage } from "../../../context/LanguageContext";

const SectionAGeneralInformation = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  // Extract canteen data from state
  const canteen = state?.canteen || {};
  const surveyDetail = state?.surveyDetail || {};
  const surveyId = state?.surveyId;
  const newSurvey = state?.newSurvey;

  // FIXED: Get location IDs from canteen object
  const districtId = canteen.district_id || state?.district_id;
  const municipalityId = canteen.municipality_id || state?.municipality_id;
  const wardId = canteen.ward_id || state?.ward_id;
  const areaId = canteen.id || state?.area_id || state?.canteenId;
  const canteenTypeId =
    canteen.canteen_type_id || canteen.canteen_type || state?.canteen_type_id;

  console.log("Kitchen Dining Section A - Received state:", {
    canteen,
    districtId,
    municipalityId,
    wardId,
    areaId,
    canteenTypeId,
    surveyId,
    newSurvey,
  });

  const [form, setForm] = useState({
    location:
      canteen?.address || canteen?.kitchen_dining_location_fulladdress || "",
    a1: "",
    a1Remarks: "",
    a1Document: null,
    a2: "",
    a2Remarks: "",
    a3: "",
    a4From: "12:00",
    a4To: "15:00",
    a5: "",
    a6: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Load existing survey data if available
  useEffect(() => {
    if (surveyDetail?.gen_data) {
      const genData = surveyDetail.gen_data;
      setForm((prev) => ({
        ...prev,
        location: genData.kitchen_dining_location_fulladdress || prev.location,
        a1:
          genData.gen_info_A1 === 1
            ? "YES"
            : genData.gen_info_A1 === 2
              ? "NO"
              : "",
        a1Remarks: genData.gen_info_A1_remark || "",
        a2:
          genData.gen_info_A2 === 1
            ? "SHGs"
            : genData.gen_info_A2 === 2
              ? "Others"
              : "",
        a2Remarks: genData.gen_info_A2_remark || "",
        a3: genData.gen_info_A3 || "",
        a4From: genData.gen_info_A4?.slice(0, 5) || "12:00",
        a4To: genData.gen_info_A4_to?.slice(0, 5) || "15:00",
        a5: genData.gen_info_A5 || "",
        a6:
          genData.gen_info_A6 === 1
            ? "YES"
            : genData.gen_info_A6 === 2
              ? "NO"
              : "",
      }));
    }
  }, [surveyDetail]);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        localStorage.setItem(
          "survey_section_a_kitchendining",
          JSON.stringify(form),
        );
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setForm((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!form.location.trim()) newErrors.location = t.global.err_address;
    if (!form.a1) newErrors.a1 = t.global.err_a1;
    if (form.a1 === "NO" && !form.a1Remarks.trim())
      newErrors.a1Remarks = t.global.err_a1;
    if (!form.a2) newErrors.a2 = t.global.err_a2;
    if (form.a2 === "Others" && !form.a2Remarks.trim())
      newErrors.a2Remarks = t.global.err_a2;
    if (!form.a3) newErrors.a3 = t.global.err_a3;
    if (!form.a4From) newErrors.a4From = t.global.err_a4;
    if (!form.a4To) newErrors.a4To = t.global.err_a4;
    if (!form.a5) newErrors.a5 = t.global.err_a5;
    if (!form.a6) newErrors.a6 = t.global.err_a6;

    // Numeric validation
    if (form.a3 && (isNaN(form.a3) || form.a3 < 0 || form.a3 > 999)) {
      newErrors.a3 = t.global.err_a3;
    }
    if (form.a5 && (isNaN(form.a5) || form.a5 < 0 || form.a5 > 999999)) {
      newErrors.a5 = t.global.err_a5;
    }

    // Time validation
    if (form.a4From && form.a4To) {
      const fromTime = new Date(`2000-01-01T${form.a4From}`);
      const toTime = new Date(`2000-01-01T${form.a4To}`);
      if (fromTime >= toTime) {
        newErrors.a4To = t.global.err_a4_order;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Get current geolocation
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (error) => {
            console.warn("Geolocation error:", error);
            reject(error);
          },
          { enableHighAccuracy: true, timeout: 10000 },
        );
      } else {
        reject(new Error("Geolocation not supported"));
      }
    });
  };

  const handleSubmit = async () => {
    // Validate location IDs
    if (
      !districtId ||
      !municipalityId ||
      !wardId ||
      !areaId ||
      !canteenTypeId
    ) {
      console.error("Missing location IDs:", {
        districtId,
        municipalityId,
        wardId,
        areaId,
        canteenTypeId,
      });
      alert(t.global.alert_missing_ids);
      return;
    }

    if (!validateForm()) {
      alert(t.global.alert_fix_errors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Get latitude & longitude automatically
      let latitude = "";
      let longitude = "";

      try {
        const location = await getCurrentLocation();
        latitude = location.latitude;
        longitude = location.longitude;
      } catch (error) {
        console.warn("Geolocation error, continuing without location:", error);
      }

      // Create FormData for API
      const fd = new FormData();

      // Location data
      fd.append("kitchen_dining_location_fulladdress", form.location);
      fd.append("reporting_date", moment().format("YYYY-MM-DD"));
      fd.append("district_id", districtId);
      fd.append("municipality_id", municipalityId);
      fd.append("ward_id", wardId);
      fd.append("area_id", areaId);
      fd.append("canteen_type_id", canteenTypeId);
      fd.append("latitude", latitude || "0");
      fd.append("longitude", longitude || "0");
      fd.append("current_section", "geninfo");

      // Parent kitchen if exists
      if (canteen?.parent_kitchen?.id) {
        fd.append("parent_kitchen_id", canteen.parent_kitchen.id);
      }

      // General info
      fd.append("gen_info_A1", form.a1 === "YES" ? "1" : "2");
      fd.append("gen_info_A1_remark", form.a1Remarks || "");
      fd.append("gen_info_A2", form.a2 === "SHGs" ? "1" : "2");
      fd.append("gen_info_A2_remark", form.a2Remarks || "");
      fd.append("gen_info_A3", form.a3);
    fd.append("gen_info_A4", form.a4From || "");
    fd.append("gen_info_A4_to", form.a4To || "");
      fd.append("gen_info_A5", form.a5);
      fd.append("gen_info_A6", form.a6 === "YES" ? "1" : "2");

      // If this is an existing survey, include survey ID
      if (surveyId) {
        fd.append("survey_id", surveyId);
      }

      console.log("Submitting kitchen dining with data:", {
        district_id: districtId,
        municipality_id: municipalityId,
        ward_id: wardId,
        area_id: areaId,
        canteen_type_id: canteenTypeId,
      });
for (let pair of fd.entries()) {
  console.log(pair[0], pair[1]);
}
      // Dispatch the action
      const response = await dispatch(createKitchenDining(fd));

      if (response?.data || response?.payload) {
        // Update completedSections in localStorage
        const completedSections = JSON.parse(
          localStorage.getItem("completedSections") || "{}",
        );
        completedSections.a = "complete";
        localStorage.setItem(
          "completedSections",
          JSON.stringify(completedSections),
        );

        // Clear draft
        localStorage.removeItem("survey_section_a_kitchendining");

        setShowSuccess(true);

        setTimeout(() => {
          setShowSuccess(false);
          navigate("/survey/sections", {
            state: {
              canteen: canteen,
              surveyDetail: {
                ...surveyDetail,
                gen_data: {
                  ...surveyDetail.gen_data,
                  is_gen_info_survey_done: 1,
                },
              },
              surveyId: surveyId,
              newSurvey: newSurvey,
            },
          });
        }, 2000);
      } else {
        throw new Error("API did not confirm success");
      }
    } catch (err) {
      console.error("Error submitting kitchen dining:", err);

      // Save as partial (offline fallback)
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.a = "partial";
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      localStorage.setItem(
        "survey_section_a_kitchendining",
        JSON.stringify({
          ...form,
          savedLocally: true,
          submittedAt: new Date().toISOString(),
        }),
      );

      alert(t.global.alert_saved_locally);
      navigate("/survey/sections", { state: { canteen, surveyId, newSurvey } });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">{t.global.success_message}</p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", {
                    state: { canteen, surveyId, newSurvey },
                  });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.global.sectionA_title}
              </h2>
              <p className="text-gray-600">{t.global.sectionA_subtitle}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full">
                {t.global.survey_type_kitchendining}
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {canteen.area_name
                  ? `${t.global.canteen_name_label} ${canteen.area_name}`
                  : t.global.na}
              </div>
              {canteen.id && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {canteen.id}
                </div>
              )}
            </div>
          </div>

          {/* Display location IDs for debugging */}
          {/* <div className="mb-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
            <p className="font-medium">{t.global.location_info}</p>
            <p>{t.global.district_id}: {districtId || t.global.na} | {t.global.municipality_id}: {municipalityId || t.global.na} | {t.global.ward_id}: {wardId || t.global.na}</p>
            <p>{t.global.area_id}: {areaId || t.global.na} | {t.global.canteen_type_id}: {canteenTypeId || t.global.na}</p>
          </div> */}

          <div className="space-y-6">
            {/* Location */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                {t.modules.kitchenCumDining.sections.A.questions.address}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t.shared.sectionA.questions.address}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="location"
                    value={form.location}
                    placeholder={t.global.location_placeholder}
                    className={`w-full border p-2 rounded ${errors.location ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    rows="3"
                    onChange={handleChange}
                    required
                  />
                  {errors.location && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.location}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* A1 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A1. {t.modules.kitchenCumDining.sections.A.questions.functional}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4 mb-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label key={option} className="inline-flex items-center">
                    <input
                      type="radio"
                      name="a1"
                      value={option}
                      checked={form.a1 === option}
                      onChange={handleChange}
                      className="mr-2 h-4 w-4"
                      required
                    />
                    <span className="font-medium">{option}</span>
                  </label>
                ))}
              </div>
              {errors.a1 && (
                <p className="text-red-500 text-sm mb-2">{errors.a1}</p>
              )}

              {form.a1 === t.global.no && (
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1">
                    {t.global.remarks_label}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="a1Remarks"
                    placeholder={t.global.remarks_functional_placeholder}
                    className={`w-full border p-2 rounded ${errors.a1Remarks ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    rows="2"
                    onChange={handleChange}
                    value={form.a1Remarks}
                  />
                  {errors.a1Remarks && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.a1Remarks}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* A2 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A2. {t.modules.kitchenCumDining.sections.A.questions.operated}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4 mb-3">
                {[t.global.shgs, t.global.others].map((option) => (
                  <label key={option} className="inline-flex items-center">
                    <input
                      type="radio"
                      name="a2"
                      value={option}
                      checked={form.a2 === option}
                      onChange={handleChange}
                      className="mr-2 h-4 w-4"
                      required
                    />
                    <span className="font-medium">{option}</span>
                  </label>
                ))}
              </div>
              {errors.a2 && (
                <p className="text-red-500 text-sm mb-2">{errors.a2}</p>
              )}

              {form.a2 === t.global.others && (
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1">
                    {t.global.remarks_label}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="a2Remarks"
                    placeholder={t.global.remarks_operated_placeholder}
                    className={`w-full border p-2 rounded ${errors.a2Remarks ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    rows="2"
                    onChange={handleChange}
                    value={form.a2Remarks}
                  />
                  {errors.a2Remarks && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.a2Remarks}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* A3 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A3. {t.modules.kitchenCumDining.sections.A.questions.staff}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="a3"
                value={form.a3}
                placeholder={t.global.staff_placeholder}
                className={`w-full border p-3 rounded ${errors.a3 ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                onChange={handleChange}
                min="0"
                required
              />
              {errors.a3 && (
                <p className="text-red-500 text-sm mt-1">{errors.a3}</p>
              )}
            </div>

            {/* A4 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A4. {t.modules.kitchenCumDining.sections.A.questions.timing}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t.global.timing_from}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    name="a4From"
                    value={form.a4From}
                    onChange={handleChange}
                    className={`w-full border p-2 rounded ${errors.a4From ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    required
                  />
                  {errors.a4From && (
                    <p className="text-red-500 text-sm mt-1">{errors.a4From}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t.global.timing_to} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    name="a4To"
                    value={form.a4To}
                    onChange={handleChange}
                    className={`w-full border p-2 rounded ${errors.a4To ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    required
                  />
                  {errors.a4To && (
                    <p className="text-red-500 text-sm mt-1">{errors.a4To}</p>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {t.global.timing_current}{" "}
                <span className="font-medium">
                  {form.a4From} {t.global.timing_to_word} {form.a4To}
                </span>
              </p>
            </div>

            {/* A5 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A5.{" "}
                {t.modules.kitchenCumDining.sections.A.questions.beneficiaries}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="a5"
                value={form.a5}
                placeholder={t.global.beneficiaries_placeholder}
                className={`w-full border p-3 rounded ${errors.a5 ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                onChange={handleChange}
                min="0"
                required
              />
              {errors.a5 && (
                <p className="text-red-500 text-sm mt-1">{errors.a5}</p>
              )}
            </div>

            {/* A6 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A6. {t.modules.kitchenCumDining.sections.A.questions.coupons}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label key={option} className="inline-flex items-center">
                    <input
                      type="radio"
                      name="a6"
                      value={option}
                      checked={form.a6 === option}
                      onChange={handleChange}
                      className="mr-2 h-4 w-4"
                      required
                    />
                    <span className="font-medium">{option}</span>
                  </label>
                ))}
              </div>
              {errors.a6 && (
                <p className="text-red-500 text-sm mt-2">{errors.a6}</p>
              )}
            </div>

            {/* Required fields note */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <span className="text-red-500">*</span>{" "}
                    {t.modules.kitchenCumDining.sections.A.mandatory ||
                      "indicates required fields. All fields must be completed before saving."}
                  </p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                onClick={() => {
                  localStorage.setItem(
                    "survey_section_a_kitchendining",
                    JSON.stringify(form),
                  );
                  navigate("/survey/sections", {
                    state: { canteen, surveyId, newSurvey },
                  });
                }}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium"
                disabled={isSubmitting}
              >
                ← {t.global.back_to_sections}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-lg font-medium transition ${
                  isSubmitting
                    ? "bg-green-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                {isSubmitting ? t.global.submitting : t.global.submit_section_a}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SectionAGeneralInformation;
